import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Ruler,
  Camera,
  Palette,
  Timer,
  Wand2,
  ShieldCheck,
} from "lucide-react";

/**
 * Entry screen shown before the wizard starts. Two goals:
 *  1) Promote the free AI concept preview so customers know exactly what
 *     they receive before committing 5 minutes of questions.
 *  2) Offer a compact "Just want an estimate?" path for drop-in visitors.
 */
export default function IntroScreen({ onStartFull, onStartQuick }) {
  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-24 grain" data-testid="builder-intro">
      <div className="container-x px-5 md:px-10 lg:px-16 max-w-5xl">
        <Link
          to="/"
          data-testid="intro-home-link"
          className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[#A1A1AA] hover:text-white"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </Link>

        <div className="mt-8 text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
          Design my wall
        </div>
        <h1 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl leading-tight">
          Free AI concept preview
          <span className="block italic text-[#D4AF37] mt-1">before you spend a dollar.</span>
        </h1>
        <p className="mt-5 text-base md:text-lg text-[#C4C4CC] max-w-2xl">
          Answer a few visual questions and we'll generate a concept preview of your custom wall — so
          you can see the direction before we ever step foot in your home. Final design, materials
          and pricing are always confirmed during your free consultation.
        </p>

        {/* What you get */}
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <PerkTile
            icon={Palette}
            title="Choose your vibe"
            desc="Pick layout, finish, colors, lighting — or tap 'help me choose' at any step."
          />
          <PerkTile
            icon={Camera}
            title="Upload your wall"
            desc="Optional photo helps Rob plan around outlets, doors and existing trim."
          />
          <PerkTile
            icon={Wand2}
            title="AI concept preview"
            desc="A visual render of your choices — a concept, not a final blueprint."
          />
          <PerkTile
            icon={ShieldCheck}
            title="Zero pressure"
            desc="Final design, materials and pricing are confirmed at the in-home consult."
          />
        </div>

        {/* Two paths */}
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <button
            type="button"
            onClick={onStartFull}
            data-testid="intro-start-full"
            className="group relative text-left rounded-2xl border border-[#D4AF37]/60 bg-gradient-to-br from-[#D4AF37]/10 via-[#0d0d0d] to-[#050505] p-7 hover:border-[#D4AF37] transition-colors overflow-hidden"
          >
            <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
              <Sparkles className="h-3.5 w-3.5" /> Recommended
            </div>
            <div className="mt-3 font-display text-2xl md:text-3xl leading-tight">
              Design with AI preview
            </div>
            <p className="mt-2 text-sm text-[#C4C4CC] max-w-md">
              About 4 minutes. Guides you through layout, style, finish, lighting and more — then
              generates your free AI concept render at the end.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#F3E5AB] font-medium">
              Start designing <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
            <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-[#A1A1AA]">
              <span className="inline-flex items-center gap-1.5"><Timer className="w-3 h-3 text-[#D4AF37]" /> ~4 minutes</span>
              <span className="inline-flex items-center gap-1.5"><Ruler className="w-3 h-3 text-[#D4AF37]" /> Feet + inches supported</span>
            </div>
          </button>

          <button
            type="button"
            onClick={onStartQuick}
            data-testid="intro-start-quick"
            className="group relative text-left rounded-2xl border border-[#27272A] bg-[#0d0d0d] p-7 hover:border-[#AA8035] transition-colors"
          >
            <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#A1A1AA]">
              <Timer className="h-3.5 w-3.5" /> 60 seconds
            </div>
            <div className="mt-3 font-display text-2xl md:text-3xl leading-tight">
              Just want an estimate?
            </div>
            <p className="mt-2 text-sm text-[#C4C4CC] max-w-md">
              Share your city, project type, budget and contact — Rob will call within one business
              day with a ballpark and next steps. No AI render on this path.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-white font-medium">
              Get an estimate <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </div>

        {/* Small print */}
        <p className="mt-8 text-[11px] text-[#8b8b92] max-w-2xl">
          Your AI preview is a stylistic concept generated from your selections and is intended for
          inspiration only. Actual materials, dimensions, structural details and pricing are
          confirmed by Swift Luxury Walls during your complimentary in-home consultation.
        </p>
      </div>
    </div>
  );
}

function PerkTile({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-xl border border-[#27272A] bg-[#0c0c0c] p-4">
      <div className="h-9 w-9 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37]">
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-3 text-sm font-medium text-white">{title}</div>
      <div className="mt-1 text-xs text-[#C4C4CC] leading-relaxed">{desc}</div>
    </div>
  );
}
